import { CartLinesUpdateEvent } from '@shopify/events';

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

async function fetchCurrentCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');

  if (cartItemsComponent) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItemsComponent.fetchCartData === 'function') {
      return cartItemsComponent.fetchCartData();
    }
  }

  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to refresh cart: ${response.status}`);
  return response.json();
}

async function addKit(button) {
  const variantIds = (button.dataset.variantIds || '')
    .split(',')
    .map((id) => Number(id))
    .filter(Boolean);

  if (!variantIds.length || button.disabled) return;

  const originalLabel = button.textContent;
  const sections = cartSectionIds();
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const lines = variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 }));

  button.disabled = true;
  button.textContent = 'Adding kit...';

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'alcove-kit',
      lines,
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: variantIds.map((id) => ({ id, quantity: 1 })),
        sections: sections.join(','),
      }),
    });

    const responseJson = await response.json();
    if (!response.ok || responseJson.status) {
      throw new Error(responseJson.description || responseJson.message || 'Unable to add kit');
    }

    const ajaxCart = await fetchCurrentCart();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        didError: false,
        itemCount: variantIds.length,
        items: responseJson.items,
        sections: responseJson.sections,
        source: 'alcove-kit',
      },
    });

    button.textContent = 'Added to cart';
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = originalLabel;
    }, 2200);
  } catch (error) {
    deferredEventPromise.reject(error);
    button.textContent = 'Try again';
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = originalLabel;
    }, 2200);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('[data-alcove-kit-add]');
  if (button) addKit(button);
});

function initializeFirstOrderOffer() {
  const offer = document.querySelector('[data-alcove-first-order]');
  if (!(offer instanceof HTMLDialogElement)) return;

  const storageKey = 'alcove-first-order-offer-v1';
  const success = offer.dataset.success === 'true';
  let engagementReady = false;
  let hasOpened = false;

  const readStoredState = () => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || 'null');
    } catch (_error) {
      return null;
    }
  };

  const storeState = (state) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes.
    }
  };

  const shouldSuppress = () => {
    const state = readStoredState();
    if (!state) return false;
    if (state.converted) return true;
    return Date.now() - Number(state.dismissedAt || 0) < 14 * 24 * 60 * 60 * 1000;
  };

  const showOffer = () => {
    if (hasOpened || (!success && shouldSuppress())) return;
    if (!success && document.querySelector('dialog[open]')) {
      window.setTimeout(showOffer, 5000);
      return;
    }

    hasOpened = true;
    if (typeof offer.showModal === 'function') offer.showModal();
    else offer.setAttribute('open', '');
  };

  const closeOffer = () => {
    if (offer.open && typeof offer.close === 'function') offer.close();
    else offer.removeAttribute('open');
  };

  offer.querySelector('[data-alcove-offer-close]')?.addEventListener('click', () => {
    if (!success) storeState({ dismissedAt: Date.now() });
    closeOffer();
  });

  offer.addEventListener('cancel', () => {
    if (!success) storeState({ dismissedAt: Date.now() });
  });

  offer.addEventListener('click', (event) => {
    if (event.target !== offer) return;
    if (!success) storeState({ dismissedAt: Date.now() });
    closeOffer();
  });

  offer.querySelector('[data-alcove-copy-code]')?.addEventListener('click', async (event) => {
    const button = event.currentTarget;
    const code = button.dataset.code || 'WELCOME10';
    await navigator.clipboard?.writeText(code);
    const status = button.querySelector('span');
    if (status) status.textContent = 'Copied';
  });

  if (success) {
    storeState({ converted: true });
    showOffer();
    return;
  }

  if (shouldSuppress()) return;

  window.setTimeout(() => {
    engagementReady = true;
  }, 12000);

  window.setTimeout(showOffer, 30000);

  const pageWrapper = document.querySelector('.page-wrapper');
  const scrollTarget = pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight ? pageWrapper : window;
  const onScroll = () => {
    const scrollable =
      scrollTarget === window
        ? document.documentElement.scrollHeight - window.innerHeight
        : scrollTarget.scrollHeight - scrollTarget.clientHeight;
    const scrollPosition = scrollTarget === window ? window.scrollY : scrollTarget.scrollTop;
    if (scrollable <= 0 || scrollPosition / scrollable < 0.55) return;
    scrollTarget.removeEventListener('scroll', onScroll);
    showOffer();
  };
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });

  document.addEventListener('mouseout', (event) => {
    if (!engagementReady || event.relatedTarget || event.clientY > 0) return;
    showOffer();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFirstOrderOffer, { once: true });
} else {
  initializeFirstOrderOffer();
}
